import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { IMG, Reveal, Overline } from './shared';
import { trackEvent } from '../../lib/analytics';

const PANELS = [
  { key: 'floor', title: 'SMARTBOT', alt: 'Harpalyk SMARTBOT intelligent floor cleaning robot', desc: 'Intelligent floor cleaning robots — vacuum, mop, AI navigation.', href: '/floor-cleaning-robots', img: IMG.heroBg, event: 'homepage_floor_click' },
  { key: 'escargot', title: 'ESCARGOT', alt: 'Harpalyk SMARTBOT Escargot commercial floor cleaning robot', desc: 'SMARTBOT commercial-grade floor robot for large spaces.', href: '/products/escargot-commercial', img: IMG.escargot, event: 'homepage_escargot_click' },
  { key: 'pool', title: 'POOLBOT', alt: 'Harpalyk POOLBOT robotic pool cleaner', desc: 'Intelligent pool cleaning robots for homes, hotels and resorts.', href: '/pool-cleaning-robots', img: IMG.pool, event: 'homepage_pool_click' },
  { key: 'window', title: 'SHINEBOT', alt: 'Harpalyk SHINEBOT window cleaning robot', desc: 'Intelligent window cleaning robots for glass at any height.', href: '/window-cleaning-robots', img: IMG.windowGlass, event: 'homepage_window_click' },
  { key: 'lawn', title: 'LAWNBOT', alt: 'Harpalyk LAWNBOT robotic lawn mower', desc: 'Intelligent robotic lawn mowers with RTK + AI Vision.', href: '/robotic-lawn-mowers', img: IMG.lawn, event: 'homepage_lawn_click' },
  { key: 'air', title: 'AIR PURIFIERS', alt: 'Harpalyk HL-AP25 Plus air purifier for homes and offices', desc: 'Clean, healthy indoor air for homes and offices.', href: '/products/hl-ap25-plus', img: IMG.airPurifier, event: 'homepage_air_click' },
];

export const Ecosystem = () => (
  <section id="products" className="bg-[#0a0a0a] text-white pt-28 md:pt-40" data-testid="hv2-ecosystem">
    <div className="max-w-6xl mx-auto px-6 md:px-12 mb-16 md:mb-24">
      <Reveal><Overline dark>Harpalyk Robotics</Overline></Reveal>
      <Reveal delay={100}>
        <h2 className="font-outfit text-5xl md:text-7xl lg:text-8xl font-medium leading-[1.0] tracking-tighter">
          ONE VISION.<br /><span className="text-white/40">A WORLD OF ROBOTICS.</span>
        </h2>
      </Reveal>
      <Reveal delay={200}>
        <p className="font-manrope text-lg text-white/50 mt-8 max-w-xl leading-relaxed">
          Harpalyk is building intelligent robotic solutions for the spaces where we live, work and move.
        </p>
      </Reveal>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3" data-testid="hv2-ecosystem-panels">
      {PANELS.map((p, i) => (
        <Link
          key={p.key}
          to={p.href}
          onClick={() => trackEvent(p.event)}
          data-testid={`hv2-panel-${p.key}`}
          className="group relative h-[60vh] lg:h-[70vh] overflow-hidden border-t border-white/10 lg:border-l"
        >
          <img src={p.img} alt={p.alt} loading="lazy" decoding="async"
            className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-700" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
          <div className="relative h-full flex flex-col justify-end p-8">
            <h3 className="font-outfit text-3xl md:text-4xl font-medium tracking-tight">{p.title}</h3>
            <p className="font-manrope text-sm text-white/60 mt-2 max-w-[220px]">{p.desc}</p>
            <span className="font-manrope inline-flex items-center gap-2 text-sm font-bold tracking-[0.2em] text-[#00AEEF] mt-5 group-hover:gap-4 transition-all duration-300">
              EXPLORE <ArrowRight className="w-4 h-4" />
            </span>
          </div>
        </Link>
      ))}
    </div>
  </section>
);

export const Commercial = () => (
  <section className="relative h-[70vh] md:h-[80vh] bg-[#0a0a0a] text-white flex items-center overflow-hidden" data-testid="hv2-commercial">
    <img src={IMG.warehouse} alt="Commercial cleaning robots operating in a large facility" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover opacity-40" />
    <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-transparent" />
    <div className="relative max-w-6xl mx-auto w-full px-6 md:px-12">
      <Reveal><Overline dark>For Business</Overline></Reveal>
      <Reveal delay={100}>
        <h2 className="font-outfit text-4xl md:text-6xl font-medium leading-[1.05] tracking-tighter max-w-2xl">
          ROBOTICS AT A LARGER SCALE.
        </h2>
      </Reveal>
      <Reveal delay={200}>
        <p className="font-manrope text-lg text-white/60 mt-6 max-w-lg leading-relaxed">
          Automation solutions for hotels, resorts, commercial pools, facility management, corporate spaces and residential developments.
        </p>
      </Reveal>
      <Reveal delay={300}>
        <Link to="/industrial-cleaning-robots" onClick={() => trackEvent('homepage_commercial_click')} data-testid="hv2-commercial-btn"
          className="font-manrope inline-block mt-10 border border-white/25 hover:bg-white hover:text-black px-8 py-4 rounded-full font-semibold transition-colors">
          EXPLORE COMMERCIAL ROBOTICS
        </Link>
      </Reveal>
    </div>
  </section>
);
